/**
 * Runtime readers for API responses.
 *
 * Roughly 120 endpoints return an unmodelled `dict`, so a TypeScript interface over a
 * response is a claim, not a guarantee. Every field a screen depends on is read
 * through these, which narrow safely and fall back rather than throwing — a missing
 * optional field must never blank a whole page.
 */

export type Dict = Record<string, unknown>;

export function asDict(value: unknown): Dict {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Dict)
    : {};
}

export function asArray(value: unknown): readonly unknown[] {
  return Array.isArray(value) ? value : [];
}

/** First present key wins, so routes that renamed a field keep working. */
export function str(source: Dict, ...keys: readonly string[]): string | null {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'string' && value.trim() !== '') return value;
  }
  return null;
}

export function strOr(fallback: string, source: Dict, ...keys: readonly string[]): string {
  return str(source, ...keys) ?? fallback;
}

export function num(source: Dict, ...keys: readonly string[]): number | null {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) {
      return Number(value);
    }
  }
  return null;
}

export function numOr(fallback: number, source: Dict, ...keys: readonly string[]): number {
  return num(source, ...keys) ?? fallback;
}

export function bool(source: Dict, ...keys: readonly string[]): boolean {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'boolean') return value;
    if (value === 'true') return true;
    if (value === 'false') return false;
  }
  return false;
}

export function strArray(source: Dict, ...keys: readonly string[]): readonly string[] {
  for (const key of keys) {
    const value = source[key];
    if (Array.isArray(value)) {
      return value.filter((entry): entry is string => typeof entry === 'string');
    }
  }
  return [];
}

/**
 * All timestamps are UTC but serialized without a timezone suffix, so `new Date()`
 * would read them as local time and shift every date by the viewer's offset.
 */
export function utcDate(source: Dict, ...keys: readonly string[]): Date | null {
  const raw = str(source, ...keys);
  if (raw === null) return null;
  const normalized = /[zZ]|[+-]\d{2}:?\d{2}$/.test(raw) ? raw : `${raw}Z`;
  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}
